import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AvaBotType } from './types';
import { AvaChatbotMessage } from './useAvaConversationMessages';

const isJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
};

export interface AskAvaWithStreamParams {
  bot_type: AvaBotType;
  conversation_id: number;
  question?: string;
  stream?: boolean;
  onStreamStart?: () => void;
  onStreamLine?: (chunk: string) => void;
  onStreamEnd?: (response: AvaChatbotMessage) => Promise<void>;
  onThinking?: (content: string, icon?: string) => void;
  onThinkingEnd?: () => void;
  onThinkingStart?: (content: string, icon?: string) => void;
  shouldExecuteCallbacks?: () => boolean;
}

interface AvaStreamLine {
  event_type: 'full_response' | 'response' | 'thinking' | 'thinking_start' | 'thinking_end';
  chunk?: string;
  content?: string;
  full_response?: string;
  icon?: string;
}

// Backend yields planner thinking events in a tight loop inside a single HTTP chunk,
// so the stream reader's synchronous for-loop calls setThinkingData N times in one
// task and React auto-batches them into a single paint — the UI flashes all events
// at once. Awaiting here yields the event loop per event so each commits separately,
// matching the CLI's progressive-reveal feel.
const THINKING_REVEAL_DELAY_MS = 60;

let aiqAssistChatController = new AbortController();

export const cancelAvaStream = () => {
  if (aiqAssistChatController) {
    aiqAssistChatController.abort('Request cancelled by user');
    aiqAssistChatController = new AbortController(); // Reset the controller for future requests.
  }
};

export const useAskAvaWithStream = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bot_type,
      conversation_id,
      question,
      onStreamStart,
      onStreamLine,
      onStreamEnd,
      onThinking,
      onThinkingEnd,
      onThinkingStart,
      shouldExecuteCallbacks
    }: AskAvaWithStreamParams) => {
      try {
        const response = await fetch(`aiq-assist/${bot_type}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/stream+json'
          },
          body: JSON.stringify({
            question: { conversation_id, question },
            stream: true
          }),
          signal: aiqAssistChatController.signal
        });

        if (!response.body) {
          throw new Error('Response does not contain a stream.');
        }

        if (shouldExecuteCallbacks?.() !== false) {
          onStreamStart?.();
        }

        // Read the response stream.
        const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let result = '';
        let done = false;

        while (!done) {
          const { done: chunkDone, value }: ReadableStreamReadResult<Uint8Array> = await reader.read();
          done = chunkDone;

          // Decode the received chunk.
          result += decoder.decode(value, { stream: true });

          // Split the result by new lines (each line is a separate chunk).
          const stream = result.split('\n');

          // Keep the last part for further processing (it might be incomplete).
          result = stream.pop() || '';

          for (let i = 0; i < stream.length; i++) {
            const streamLine = stream[i];
            if (!isJsonString(streamLine)) {
              console.warn('Invalid JSON string:', streamLine);
              continue;
            }
            let currentStreamLine: AvaStreamLine;
            try {
              currentStreamLine = JSON.parse(streamLine);
            } catch (error) {
              console.error('Error parsing JSON:', error);
              continue;
            }
            const { event_type, chunk, content, full_response, icon } = currentStreamLine;

            try {
              switch (event_type) {
                case 'thinking':
                  if (content && shouldExecuteCallbacks?.() !== false) {
                    onThinking?.(content, icon);
                  }
                  break;
                case 'thinking_start':
                  if (content && shouldExecuteCallbacks?.() !== false) {
                    onThinkingStart?.(content, icon);
                  }
                  break;
                case 'thinking_end':
                  if (shouldExecuteCallbacks?.() !== false) {
                    onThinkingEnd?.();
                  }
                  break;
                case 'response':
                  if (chunk && shouldExecuteCallbacks?.() !== false) {
                    onStreamLine?.(chunk);
                  }
                  break;
                case 'full_response':
                  if (full_response && shouldExecuteCallbacks?.() !== false) {
                    const fullStreamResponse: AvaChatbotMessage = JSON.parse(full_response);
                    onStreamEnd?.(fullStreamResponse);
                  }
                  break;
                default:
                  console.warn(`Unknown AVA streaming event type: ${event_type}`);
              }
            } catch (error) {
              console.error('Error dispatching stream event:', error);
            }

            // Pace only between consecutive thinking-family events in the same
            // chunk. The delay exists to break React auto-batching so each
            // thinking row reveals separately — it must never be applied before
            // response / full_response, which need full-speed token streaming.
            if (event_type !== 'thinking' && event_type !== 'thinking_start') continue;
            const nextLine = stream[i + 1];
            if (!nextLine || !isJsonString(nextLine)) continue;
            let nextType: AvaStreamLine['event_type'] | undefined;
            try {
              nextType = (JSON.parse(nextLine) as AvaStreamLine).event_type;
            } catch {
              continue;
            }
            if (nextType === 'thinking' || nextType === 'thinking_start') {
              await new Promise(resolve => setTimeout(resolve, THINKING_REVEAL_DELAY_MS));
            }
          }
        }

        return response;
      } finally {
        aiqAssistChatController = new AbortController();
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['aiq-assist/messages'] })
  });
};
